import Papa, { ParseResult } from "papaparse";
import { message } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../core/data/redux/store";
import { createCSVPunch, createPunch } from "../core/data/redux/attendance/punch/punchAction";
import { useEffect } from "react";
import { getEmployees } from "../core/data/redux/employee/employeeAction";

const convertURLToFakeFile = (url?: string) => {
  if (!url) {
    return null;
  }
  const blob = new Blob([], { type: "image/jpeg" });
  return new File([blob], url.split("/").pop() || "image.jpg", {
    type: "image/jpeg",
  });
};

const formatDate = (inputDate: string) => {
  const date = new Date(inputDate);
  if (isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}/${month}/${day}`;
};

const mapCSVRowToPunch = (row: any) => {
  const parseToSQLDateTime = (dateStr: string, timeStr?: string) => {
    if (!dateStr) return null;

    // Ensure date is YYYY-MM-DD
    const dateMatch = /^\d{4}-\d{2}-\d{2}$/.test(dateStr);
    if (!dateMatch) return null;

    let time = "00:00:00";

    if (timeStr) {
      const cleanTime = timeStr.trim();

      // HH:mm
      if (/^\d{1,2}:\d{2}$/.test(cleanTime)) {
        time = `${cleanTime.padStart(5, "0")}:00`;
      }
      // HH:mm:ss
      else if (/^\d{1,2}:\d{2}:\d{2}$/.test(cleanTime)) {
        time = cleanTime.padStart(8, "0");
      }
    }

    return `${dateStr} ${time}`;
  };


  return {
    EmployeeId: row.EmployeeId ? String(row.EmployeeId) : undefined,
    PunchDate: parseToSQLDateTime(row.PunchDate),
    PunchTime: parseToSQLDateTime(row.PunchDate, row.PunchIn),
    PunchOut: parseToSQLDateTime(row.PunchDate, row.PunchOut),
    PunchType: 'IN',
    EmployeeCode: row.EmployeeNumber?.toString(),
    Longitude: row.Longitude?.toString(),
    Latitude: row.Latitude?.toString(),
    CreatedBy: row.CreatedBy?.toString(),
    file: row.CheckInImagePath ? convertURLToFakeFile(row.CheckInImagePath) : null,
  };
};





export const usePunchCSVImport = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { employees } = useSelector((state: any) => state.employee);

  useEffect(() => {
    dispatch(getEmployees());
  }, [])

  const handleCSVImport = (file: File) => {
    message.loading({ content: "Importing punch records...", key: "import" });

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async function (results: ParseResult<any>) {
        const records = results.data as any[];
        let validCount = 0;
        let invalidCount = 0;

        for (let row of records) {
          const validationErrors: string[] = [];

          const normalize = (str: string) =>
            (str || "").replace(/\s+/g, " ").trim().toLowerCase();



          const employeeObj = employees?.find(
            (e: any) =>
              normalize(`${e?.FirstName || ""} ${e?.LastName || ""}`) ===
              normalize(row.Employee)
          );
          row.EmployeeId = employeeObj?.EmployeeID || null;
          if (!row.Employee) validationErrors.push("Employee is required.");
          if (!row.EmployeeNumber) validationErrors.push("Employee Number is required.");


          if (row.PunchDate) {
            const parsedDate = new Date(row.PunchDate);
            if (!isNaN(parsedDate.getTime())) {
              const yyyy = parsedDate.getFullYear();
              const mm = String(parsedDate.getMonth() + 1).padStart(2, "0");
              const dd = String(parsedDate.getDate()).padStart(2, "0");
              row.PunchDate = `${yyyy}-${mm}-${dd}`;
            } else {
              validationErrors.push(`Invalid Punch Date: ${row.PunchDate}`);
            }
          } else {
            validationErrors.push("Punch Date is required.");
          }


          const timeRegex = /^\d+:\d+(:\d+)?$/;


          if (row.PunchIn && !timeRegex.test(row.PunchIn)) {
            validationErrors.push("Invalid Punch In time format.");
          }


          if (row.PunchOut && !timeRegex.test(row.PunchOut)) {
            validationErrors.push("Invalid Punch Out time format.");
          }

          // if (!row.CreatedBy) validationErrors.push("Created By is required.");


          if (validationErrors.length > 0) {
            message.warning(
              `Skipping row for EmployeeId ${row.EmployeeId || "unknown"}: ${validationErrors.join(", ")}`
            );
            invalidCount++;
            continue;
          }

          // ✅ Convert row into punch object
          const punchData = mapCSVRowToPunch(row);

          try {
            const result = await dispatch(createCSVPunch(punchData));

            if (createPunch.rejected.match(result)) {
              const errorMsg =
                typeof result.payload === "string"
                  ? result.payload
                  : "Failed to import punch record";
              message.error(errorMsg);
              invalidCount++;
            } else {
              validCount++;
            }
          } catch (error) {
            console.error("Error importing punch:", error);
            message.error("An unexpected error occurred while importing punch data.");
            invalidCount++;
          }
        }

        // ✅ Final summary
        message.success({
          content: `Imported: ${validCount}, Skipped: ${invalidCount}`,
          key: "import",
          duration: 4,
        });
      },
    });
  };



  return {
    handleCSVImport,
  };
};
